import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import DeleteConfirmPopUp from "../../common/deleteConfirmPopUp";
import CreateCoursePopup from "../../common/createPopup";
import {
  getTopicsBySubjectKey,
  updateTopic,
  deleteTopic,
} from "../../../helper/apicalls/course";
import Breadcrumbs from "../../common/Breadcrumbs";
import { HomeOutlinedIcon } from "../../../icons";
import ActionButtons from "../../common/ActionButtons";
import "./../../../styles/admin/topics.scss";
import GlobalLoader from "../../common/GlobalLoader";
import EmptyPage from "../../common/emptyPage";

const TopicList = ({
  hideBreadcrumbs = false,
  cacheTopicsData = [],
  setCacheTopicsData,
  showActionBtn,
  role,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { key, subkey } = useParams();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [courseName, setCourseName] = useState("");
  const [subjectName, setSubjectName] = useState("");

  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [popupHeaderTitle, setPopupHeaderTitle] = useState("");
  const [popupBtnText, setPopupBtnText] = useState("");
  const [popupInitialValue, setPopupInitialValue] = useState("");
  const [selectedTopic, setSelectedTopic] = useState(null);

  const isEvalPath = location.pathname.startsWith("/admin/eval");

  useEffect(() => {
    fetchTopics();
  }, [key, subkey, location]);

  const fetchTopics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getTopicsBySubjectKey(subkey);

      if (Array.isArray(data)) {
        setTopics(data);
        setCacheTopicsData(data);
        if (data.length > 0 && data[0].subject) {
          setSubjectName(data[0].subject.name);
          if (data[0].subject.course) {
            setCourseName(data[0].subject.course.name);
          }
        }
      } else if (
        data &&
        typeof data === "object" &&
        data.hasOwnProperty("id")
      ) {
        setTopics([data]);
        if (data.subject) {
          setSubjectName(data.subject.name);
          if (data.subject.course) {
            setCourseName(data.subject.course.name);
          }
        }
      } else {
        setTopics([]);
      }
    } catch (err) {
      console.error("Error fetching topics:", err);
      setError("Failed to fetch topics");
      setTopics([
        { id: 1, name: "Algebra" },
        { id: 2, name: "Integration" },
        { id: 3, name: "Differentiation" },
        { id: 4, name: "Reciprocals" },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteButtonClick = (topic) => {
    setIsConfirmDeleteOpen(true);
    setSelectedTopic(topic);
  };

  const handleDelete = async (event) => {
    event.stopPropagation();
    try {
      await deleteTopic(selectedTopic.key);
      fetchTopics();
      setIsConfirmDeleteOpen(false);
    } catch (err) {
      console.error("Error deleting topic:", err);
      setError("Failed to delete topic");
    }
  };

  const handleEdit = (topic) => {
    setSelectedTopic(topic);
    setPopupHeaderTitle(`Edit ${topic.name}`);
    setPopupBtnText("Update");
    setPopupInitialValue(topic.name);
    setOpenPopup(true);
  };

  const handleUpdate = async (updatedName, totalNumberQuizes) => {
    try {
      const updatedTopic = {
        ...selectedTopic,
        name: updatedName,
        total_number_quizes: totalNumberQuizes ? parseInt(totalNumberQuizes, 10) : 0,
      };

      await updateTopic(updatedTopic);
      fetchTopics();
    } catch (err) {
      console.error("Error updating topic:", err);
      setError("Failed to update topic");
    }
    setOpenPopup(false);
    setPopupInitialValue("");
    setSelectedTopic(null);
  };

  const handleTopicClick = (topic) => {
    if (role === "admin") {
      if (isEvalPath) {
        navigate(
          `/admin/eval/${key}/subject/${subkey}/topic/${topic.key}`,
        );
      } else {
        navigate(
          `/admin/course/${key}/subject/${subkey}/topic/${topic.key}`,
        );
      }
    } else {
      navigate(
        `/institution/course/${key}/subject/${subkey}/topic/${topic.key}`,
      );
    }
  };

  const handleClosePopup = () => {
    setOpenPopup(false);
    setPopupInitialValue("");
    setSelectedTopic(null);
  };

  const breadcrumbBaseCoursePath = isEvalPath ? "/admin/eval" : "/admin/course";

  const breadcrumbLinks = [
    { icon: <HomeOutlinedIcon />, path: "/admin/courses" },
    {
      label: courseName || "Course " + key,
      path: `${breadcrumbBaseCoursePath}/${key}`,
    },
    { label: subjectName || "Subject " + subkey, active: true },
  ];

  return (
    <>
      {loading && <GlobalLoader visible={loading} text="Loading..." />}
      {topics?.length > 0 ? (
        <div className="topics-container">
          {!hideBreadcrumbs && (
            <Breadcrumbs
              links={breadcrumbLinks}
              showGoUpButton={true}
              goUpPath={`/admin/course/${key}`}
            />
          )}

          <div className="topics-card">
            <div className="topics-card-header">
              <span>Name of the topics</span>
              {showActionBtn && (
                <span style={{ marginRight: "16px" }}>Actions</span>
              )}
            </div>
            <ul className="topics-list">
              {topics.map((top) => (
                <li key={top.id} className="topic-item">
                  <span
                    className="topic-name"
                    onClick={() => handleTopicClick(top)}
                  >
                    {top.name}
                  </span>
                  {showActionBtn && (
                    <div className="topic-actions">
                      <span
                        className="action-link"
                        onClick={() => handleEdit(top)}
                      >
                        Edit
                      </span>
                      <span
                        className="action-link"
                        onClick={() => handleDeleteButtonClick(top)}
                      >
                        Delete
                      </span>
                    </div>
                  )}
                </li>
              ))}
            </ul>
            {showActionBtn && (
              <div className="topics-card-footer">
                <ActionButtons
                  context="topic"
                  courseKey={key}
                  subjectKey={subkey}
                  onCreateSuccess={fetchTopics}
                  buttonLabel="Add Topic"
                />
              </div>
            )}
          </div>
         {isConfirmDeleteOpen&& <DeleteConfirmPopUp
            isConfirmDeleteOpen={isConfirmDeleteOpen}
            setIsConfirmDeleteOpen={setIsConfirmDeleteOpen}
            handleDelete={handleDelete}
          />}
        { openPopup&& <CreateCoursePopup
            open={openPopup}
            handleClose={handleClosePopup}
            title="Topic"
            headerTitle={popupHeaderTitle}
            buttonText={popupBtnText}
            initialValue={popupInitialValue}
            initialTotalNumberQuizes={selectedTopic ? selectedTopic.total_number_quizes : ""}
            onSave={handleUpdate}
          />}
        </div>
      ) : (
        <>
          <EmptyPage content="No topics Available" />
          <div className="topics-card-footer">
            <ActionButtons
              context="topic"
              courseKey={key}
              subjectKey={subkey}
              onCreateSuccess={fetchTopics}
              buttonLabel="Add Topic"
            />
          </div>
        </>
      )}
    </>
  );
};

export default TopicList;
